import { useMsal } from "@azure/msal-react";
import { CommandBarButton, DefaultButton, IButtonStyles, IOverflowSetItemProps, IOverflowSetStyles, IPanelProps, IRenderFunction, MessageBar, MessageBarType, OverflowSet, Panel, PanelType, PrimaryButton, ProgressIndicator, Separator, Stack, Text, mergeStyles, useTheme } from "@fluentui/react";
import { RootState } from "app/store";
import PDF from 'assets/SVGPack/hslPDF.svg';
import FileImportDialog, { IFileBlob } from "components/controls/FileImportDialog";
import { saveAs } from "file-saver";
import { emitCustomEvent, fadeIn, panelButtonsStyles } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { setProcessing } from "global/Reducer/statusSlice";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { IDocument } from "./IDocument";

interface IDocumentManagement {
    isOpen: boolean;
    onCancel: () => void;
    planId: string;
    onChange: (documents: IDocument[]) => void;
    documents?: IDocument[];
    mergeDocument?: Blob;
}

const DocumentManagement: React.FunctionComponent<IDocumentManagement> = (props) => {
    const theme = useTheme();
    const { planId } = props;
    const { instance } = useMsal();
    const dispatch = useDispatch();

    const tokenSource = new MsalTokenSource(instance);
    const appDataSource = new AppDataSource(tokenSource);

    const isMerging = !!props.mergeDocument;
    const state = useSelector((state: RootState) => state);

    const [viewModel, setViewModel] = useState({ documents: [] as IDocument[] });

    useEffect(() => {
        if (props.isOpen) {
            const documents = structuredClone(props.documents) ?? [];

            if (props.mergeDocument)
                documents.forEach(eachDoc => eachDoc.status = "remove");

            setViewModel({ documents });

            if (props.mergeDocument) {
                const blobName = (props.mergeDocument as IFileBlob)?.name ?? `merged.pdf`;

                const newDoc: IDocument = {
                    id: uuidv4(),
                    name: blobName,
                    size: props.mergeDocument.size,
                    loadedBytes: 0,
                    status: "untracked",
                    blobName: `account/${state.data.account?.accountid}/snapshot/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/checklist/${planId}/documents/${blobName}`,
                    blob: props.mergeDocument
                };

                setViewModel(xViewModel => {
                    const nViewModel = { ...xViewModel };
                    nViewModel.documents = [...nViewModel.documents, newDoc];
                    return nViewModel;
                });
            }
        }
    }, [props.isOpen, props.mergeDocument]);

    const overflowStyles: IOverflowSetStyles = {
        root: { display: 'flex', justifyContent: 'flex-end' },
        overflowButton: {}
    }

    const onRenderItemStyles = {
        root: { padding: '1px' },
    };

    const cardClassName = mergeStyles({
        cursor: "pointer",
        border: '1px solid',
        borderColor: theme.schemes?.default?.semanticColors.inputBorder,
        borderRadius: '5px',
        backgroundColor: theme.schemes?.soft?.semanticColors.menuBackground,
        padding: '5px',
        maxWidth: '100%',
        transition: 'all 0.3s ease-out',
        ':hover': {
            backgroundColor: theme.schemes?.soft?.semanticColors.menuItemBackgroundHovered,
        }
    });


    const onRenderDocumentItem = (document: IDocument) => {
        const menuItems: IOverflowSetItemProps[] = [];
        const uploading = document.status === "uploading";
        const untracked = document.status === "untracked";

        menuItems.push({
            key: 'download',
            name: 'Download',
            onClick: async () => {
                try {
                    dispatch(setProcessing(true));
                    const blob = await appDataSource.blob.downloadBlob(document.blobName, undefined, "generated")
                    saveAs(blob.blob, document.name);
                } catch (error) {
                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });
                } finally {
                    dispatch(setProcessing(false));

                }

            }
        });

        menuItems.push({
            key: 'remove',
            name: 'Remove',
            onClick: () => {
                setViewModel(xValue => {
                    return {
                        ...xValue,
                        documents: xValue.documents.filter(t => t.id !== document.id)
                    };
                });

            }
        });


        return <Stack
            horizontal
            verticalAlign="center"
            key={document.id}
            className={cardClassName}
        >
            <Stack.Item>
                <img src={PDF} style={{ width: '48px', padding: '5px' }} />
            </Stack.Item>

            <Stack.Item grow={1} styles={{ root: { paddingLeft: '8px', paddingRight: '8px' } }}>
                {document.error &&
                    <MessageBar messageBarType={MessageBarType.error} >
                        <Text>{document.error}</Text>
                    </MessageBar>
                }
                {(uploading || untracked) &&
                    <ProgressIndicator
                        label={document.name}
                        description={untracked ? "Untracked" : "Uploading..."}
                        percentComplete={document.loadedBytes / document.size}
                        styles={{ root: {} }}
                    />
                }
                {!(uploading || untracked) &&
                    <Text>{document.name}</Text>
                }
                {document.status === "remove" &&
                    <MessageBar messageBarType={MessageBarType.warning} >
                        <Text >This record will be dissociated.</Text>
                    </MessageBar>
                }
            </Stack.Item>
            <Stack.Item>
                <OverflowSet
                    styles={overflowStyles}
                    onRenderOverflowButton={(props, defaultRender) => {
                        const buttonStyles: Partial<IButtonStyles> = {
                            root: {
                                minWidth: 0,
                                padding: '0',
                                alignSelf: 'stretch',
                                height: '100%',
                                width: 'auto',
                                backgroundColor: 'transparent'
                            },
                        };

                        return (
                            <CommandBarButton
                                ariaLabel="More items"
                                role="menuitem"
                                styles={buttonStyles}
                                menuIconProps={{ iconName: 'More' }}
                                menuProps={{ items: props! }} />
                        );
                    }}
                    onRenderItem={item => {
                        if (item.onRender) {
                            return item.onRender(item);
                        }

                        return (
                            <CommandBarButton
                                role="menuitem"
                                aria-label={item.name}
                                styles={onRenderItemStyles}
                                iconProps={{ iconName: item.icon }}
                                onClick={item.onClick} />
                        );
                    }}
                    overflowItems={menuItems} />
            </Stack.Item>
        </Stack>;
    }

    const uploadBlob = () => {
        const documents = viewModel.documents.filter(q => q.status === 'untracked' && !!q.blob);

        documents.forEach(eachDocument => {
            const { blob, blobName, name, id } = eachDocument;
            appDataSource.attachEMChecklistPDF(
                blob,
                blobName,
                (progress) => {
                    setViewModel(xViewModel => {
                        const nViewModel = { ...xViewModel };
                        nViewModel.documents = nViewModel.documents.map(eachDoc => {
                            if (eachDoc.id === id) {
                                const document: IDocument = {
                                    ...eachDoc,
                                    loadedBytes: progress.loadedBytes,
                                    status: 'uploading'
                                };
                                return document;
                            }
                            return eachDoc;
                        });
                        return nViewModel;
                    });
                }
            ).then(value => {
                setViewModel(xViewModel => {
                    const nViewModel = { ...xViewModel };
                    nViewModel.documents = nViewModel.documents.map(eachDoc => {
                        if (eachDoc.id === id) {
                            const document: IDocument = {
                                ...eachDoc,
                                status: 'ready'
                            };
                            return document;
                        }
                        return eachDoc;
                    });
                    return nViewModel;
                });
            }).catch(error => {
                setViewModel(xViewModel => {
                    const nViewModel = { ...xViewModel };
                    nViewModel.documents = nViewModel.documents.map(eachDoc => {
                        if (eachDoc.id === id) {
                            const document: IDocument = {
                                ...eachDoc,
                                error: error.toString(),
                                loadedBytes: -1,
                                status: 'error'
                            };
                            return document;
                        }

                        return eachDoc;
                    });
                    return nViewModel;
                });
            });
        })

    }

    const onRenderBody = () => {
        return <div>
            <Separator />
            {isMerging &&
                <MessageBar>
                    Any modifications made to the PDF, including annotations, will be saved. Additionally, the current associated PDF files will be replaced with the merged version currently being displayed on the interface.
                </MessageBar>
            }
            {!isMerging && <>
                <FileImportDialog
                    maxFiles={10}
                    showAttached={false}
                    onDropAsBlob={(blob) => {
                        const newDoc: IDocument = {
                            id: uuidv4(),
                            name: blob.name,
                            size: blob.size,
                            loadedBytes: 0,
                            status: "untracked",
                            blobName: `account/${state.data.account?.accountid}/snapshot/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/checklist/${planId}/documents/${blob.name}`,
                            blob: blob,
                        };

                        setViewModel(xViewModel => {
                            const nViewModel = { ...xViewModel };
                            nViewModel.documents = [...nViewModel.documents, newDoc];
                            return nViewModel;
                        });
                    }}
                />
            </>
            }
            <Stack tokens={{ childrenGap: '10px' }}>
                {viewModel.documents.length > 0 &&
                    <>
                        <Separator />
                        <Text variant="large">Documents</Text>
                    </>
                }
                {viewModel.documents.map(eachDocument => {
                    return onRenderDocumentItem(eachDocument);
                })}
            </Stack>
        </div>;
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (footerProps, footerDefaultRender) => {
        const anythingToUpload = viewModel.documents.some(q => q.status === "untracked");

        return <div className={fadeIn}>
            {!anythingToUpload &&
                <PrimaryButton
                    text='Done'
                    disabled={viewModel.documents.some(q => !(q.status === "ready" || q.status === "remove" || q.status === "error"))}
                    styles={panelButtonsStyles}
                    onClick={async () => {
                        const documents = viewModel.documents.filter(q => !q.error && q.status === 'ready');
                        props.onChange(documents);
                    }}
                />
            }
            {anythingToUpload &&
                <PrimaryButton
                    text='Upload'
                    disabled={viewModel.documents.some(q => q.status === "uploading")}
                    styles={panelButtonsStyles}
                    onClick={async () => {
                        uploadBlob();
                    }}
                />
            }
            <DefaultButton
                text='Cancel'
                styles={panelButtonsStyles}
                onClick={async () => {
                    props.onCancel?.();
                }}
            />
        </div>
    }

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
        headerText="Document Management"
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
        layerProps={{ eventBubblingEnabled: true }}
    >
        {onRenderBody()}
    </Panel>

}

export default DocumentManagement;